import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link as LinkIcon, Plus, Trash, Pencil, Check, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ManageLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDesc, setNewDesc] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [editingLink, setEditingLink] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [editUrl, setEditUrl] = useState('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API}/links`);
      setLinks(response.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLink = async (e) => {
    e.preventDefault();
    if (!newDesc.trim() || !newUrl.trim()) {
      toast.error('Both fields are required');
      return;
    }
    
    // Auto-prepend https:// if no protocol exists
    let formattedUrl = newUrl.trim();
    if (!formattedUrl.match(/^https?:\/\//i)) {
      formattedUrl = 'https://' + formattedUrl;
    }

    try {
      await axios.post(`${API}/links`, {
        description: newDesc.trim(),
        url: formattedUrl
      });
      toast.success('Link added successfully!');
      setNewDesc('');
      setNewUrl('');
      fetchLinks();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to add link';
      toast.error(errorMsg);
    }
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    try {
      await axios.delete(`${API}/links/${id}`);
      toast.success('Link deleted');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to delete link');
    }
  };

  const startEdit = (link) => {
    setEditingLink(link.id);
    setEditDesc(link.description || link.title);
    setEditUrl(link.url);
  };

  const handleUpdateLink = async (id) => {
    if (!editDesc.trim() || !editUrl.trim()) {
      toast.error('Both fields are required');
      return;
    }

    let formattedUrl = editUrl.trim();
    if (!formattedUrl.match(/^https?:\/\//i)) {
      formattedUrl = 'https://' + formattedUrl;
    }

    try {
      await axios.put(`${API}/links/${id}`, {
        description: editDesc.trim(),
        url: formattedUrl
      });
      toast.success('Link updated');
      setEditingLink(null);
      fetchLinks();
    } catch (err) {
      toast.error('Failed to update link');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2 border-b pb-3">
        <LinkIcon size={28} className="text-gray-800" weight="bold" />
        <div>
          <h2 className="text-xl font-bold text-gray-800">Manage Links</h2>
          <p className="text-sm text-gray-500">Add links visible to all customers in their "Links" tab</p>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-6">Loading links...</p>
      ) : (
        <div className="space-y-3 mb-6">
          {links.map((link) => (
            <div key={link.id} className="p-4 border border-gray-150 rounded-xl bg-white flex items-center justify-between hover:shadow-sm transition-all">
              {editingLink === link.id ? (
                <div className="flex-1 space-y-2 mr-4">
                  <input
                    type="text"
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Link description"
                  />
                  <input
                    type="text"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="URL"
                  />
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-800 text-base">{link.title || link.description}</h3>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline text-sm font-medium block truncate mt-0.5"
                  >
                    {link.url}
                  </a>
                </div>
              )}

              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                {editingLink === link.id ? (
                  <>
                    <button
                      onClick={() => handleUpdateLink(link.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <Check size={20} weight="bold" />
                    </button>
                    <button
                      onClick={() => setEditingLink(null)}
                      className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X size={20} weight="bold" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(link)}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Pencil size={20} />
                    </button>
                    <button
                      onClick={() => handleDeleteLink(link.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash size={20} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Link Inputs */}
      <form onSubmit={handleAddLink} className="space-y-3 pt-4 border-t">
        <input
          type="text"
          placeholder="Link description (e.g., Telegram Group)"
          value={newDesc}
          onChange={(e) => setNewDesc(e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50/50"
        />
        <input
          type="text"
          placeholder="URL (e.g., https://t.me/group)"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50/50"
        />
        <button
          type="submit"
          className="w-full bg-[#25D366] hover:bg-green-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
        >
          <Plus size={20} weight="bold" /> Add Link
        </button>
      </form>
    </div>
  );
}
